<script>
import SettingsSidebar from "@/components/rubrics/newRubric/settings_sidebar.vue";
import Settings from "@/components/rubrics/newRubric/settings.vue";
import RubricForm from "@/components/rubrics/rubric_form.vue";
import { generateRubricByAi } from "@/api";
import RubricAiLoadingFullscreen from "@/components/rubrics/components/rubric_ai_loading_fullscreen.vue";
import RubricFullscreenModal from "@/components/rubrics/components/rubric_fullscreen_modal.vue";
import RubricFormActions from "@/components/rubrics/newRubric/components/rubric_form_actions.vue";
import RubricSettingsTab from "@/components/rubrics/components/rubric_settings_tab.vue";
import RubricPreviewTab from "@/components/rubrics/components/rubric_preview_tab.vue";
import rubricFormStateMixin from "@/mixins/rubricFormStateMixin";

export default {
  name: "RubricAi",
  components: {
    RubricForm,
    Settings,
    SettingsSidebar,
    RubricAiLoadingFullscreen,
    RubricFullscreenModal,
    RubricFormActions,
    RubricSettingsTab,
    RubricPreviewTab,
  },
  mixins: [rubricFormStateMixin],
  inject: ["clearRubricSettings", "newRubricSettings"],
  emits: ["createRubric", "close"],
  data() {
    return {
      step: 0,
      settings: {
        category: true,
        grade: true,
        intensity: true,
        language: true,
      },
      specifications: "",
      aiLoading: false,
      rubric: null,
    };
  },
  beforeMount() {
    this.clearRubricSettings();
  },
  methods: {
    async generateRubric() {
      this.step = 1;
      this.aiLoading = true;

      try {
        const { data } = await generateRubricByAi({
          category:
            this.newRubricData.categories[
              this.newRubricSettings.selectedCategoryIdx
            ].value,
          gradeLevel: this.newRubricSettings.selectedGradeLevel,
          intensity:
            this.newRubricData.intensities[
              this.newRubricSettings.selectedIntensityIdx
            ].value,
          LanguageId: this.newRubricSettings.selectedLanguageId,
          specifications: this.specifications || undefined,
        });
        this.rubric = data;
        this.updateSettingsFromRubric(data);
        this.aiLoading = false;
        this.emitRubricBuilderOpened();
      } catch (e) {
        console.error("Error creating rubric:", e);
        this.$showToastError(
          "There was a problem creating the rubric. Please try again, or contact support",
          { title: "Rubric Creation failed" }
        );
        this.step = 0;
        this.aiLoading = false;
      }
    },
    goBackToSettings() {
      this.step = 0;
      this.aiLoading = false;
      // Don't clear specifications or settings - user can continue from where they left off
    },
    onSaveSettings() {
      if (this.step === 1) {
        this.generateRubric();
      }
    },
  },
};
</script>

<template>
  <div class="rubric-ai-wrapper">
    <!-- Fullscreen Loading Overlay -->
    <RubricAiLoadingFullscreen
      v-show="step === 1 && aiLoading"
      @back="goBackToSettings"
    />

    <!-- Fullscreen Rubric Form -->
    <RubricFullscreenModal
      v-show="step === 1 && !aiLoading"
      ref="fullscreenModal"
      mode="create"
      :show-tabs="true"
      @cancel="$emit('close')"
      @save="$refs.rubricForm.handleSubmit()"
      @back="goBackToSettings"
    >
      <!-- Edit Tab Content -->
      <template #default>
        <rubric-form
          v-if="rubric"
          :key="`ai-form-${rubric?.id || 'new'}`"
          ref="rubricForm"
          :rubric="rubric"
          :editing="true"
          :hide-actions="true"
          :initial-data="rubricFormData"
          @rubricSubmitted="$emit('createRubric', $event)"
          @formDataUpdate="handleFormDataUpdate"
          @switchToFirstTab="$refs.fullscreenModal.activeTab = 0"
          @close="$emit('close')"
        />
      </template>

      <!-- Preview Tab Content -->
      <template #preview>
        <RubricPreviewTab
          :key="`preview-${rubricFormData.name}-${
            rubricFormData.criterias?.length || 0
          }`"
          :rubric="rubricFormData"
        />
      </template>

      <!-- Settings Tab Content -->
      <template #settings>
        <RubricSettingsTab grade intensity language />
      </template>
    </RubricFullscreenModal>

    <!-- Normal Modal Content -->
    <div v-show="step === 0">
      <eg-b-row class="full-width rubric-form-body" no-gutters>
        <SettingsSidebar v-bind="settings" @onSave="onSaveSettings" />
        <eg-b-col cols="12" class="rubric-form-content">
          <!-- Specifications Section -->
          <div class="mb-4">
            <h3 class="paragraph-m">{{ $t("rubrics.whatToGrade") }}</h3>
            <p class="paragraph-s color-text-neutral-subtle">
              {{ $t("rubrics.rubricAiInstructions") }}
            </p>
            <eg-b-textarea
              v-model="specifications"
              class="grade-instructions paragraph-s"
              :placeholder="$t('rubrics.rubricAiPlaceholder')"
              :rows="5"
              maxlength="1000"
            />
            <div class="text-muted small mt-1">
              {{ specifications.length }}/1000 words max
            </div>
          </div>
          <Settings category grade intensity language />
        </eg-b-col>
      </eg-b-row>
      <!-- Action Buttons -->
      <RubricFormActions
        :on-cancel="() => $emit('close')"
        :on-submit="generateRubric"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.rubric-ai-wrapper {
  position: relative;
  width: 100%;
}
.grade-instructions {
  color: var(--color-text-neutral-secondary);
  &::placeholder {
    color: var(--color-text-neutral-disabled);
  }
}
.color-text-neutral-subtle {
  color: var(--color-text-neutral-secondary, #566370);
}
</style>
